/**
 * voucherFormTabs — CHIA FIELD CỦA FORM CHỨNG TỪ THÀNH MASTER / TAB CHI TIẾT / TAB PHỤ.
 *
 * Chứng từ có rất nhiều field; dồn hết lên phần master thì form dài lê thê. InputBase giải bằng
 * `field.tab`, và bộ mới giữ nguyên phương ngữ đó qua `field.formTab`:
 *
 *   - field KHÔNG khai tab, HOẶC tab là "Thông tin chung"  -> nằm ở phần MASTER (như trước)
 *   - field khai tab khác                                   -> gom thành TAB, xếp SAU các tab chi tiết
 *
 * Field TỔNG CỘNG (isSummary) luôn ở chân form, kể cả khi có khai tab — nó là con số kết luận của cả
 * chứng từ, không thuộc về một tab nào.
 *
 * File KHÔNG import gì để test node chạy được trên chính mã nguồn này.
 */

/**
 * PHƯƠNG NGỮ TAB CỦA BỘ CŨ — nguồn chuẩn là `FormBase.js:100-121`, nơi InputBase chia form làm ba phần:
 *
 *   f.tab === 'master'   -> phần 1: thông tin master, KHÔNG nằm trong tab nào
 *   f.tab === 'summary'  -> phần 3: dải tổng cộng ở chân form
 *   f.tab === 'footer'   -> (bộ cũ có thêm một dải nữa dưới summary)
 *   còn lại              -> phần 2: các tab
 *
 * Bộ mới nhận thêm 'thông tin chung' cho phần master vì nhiều khai báo dùng tên tiếng Việt đó.
 */
const MAIN_TAB_NAMES = ['master', 'thông tin chung', 'thong tin chung'];

/**
 * Tab đưa field xuống DẢI CHÂN FORM. Gộp luôn 'footer' của bộ cũ vào đây: chân form của bộ mới đã là
 * một dải chứa field tổng cộng + nút Lưu/Thoát, tách thêm một dải nữa chỉ làm form chật.
 */
const SUMMARY_TAB_NAMES = ['summary', 'footer'];

/** Tên tab đã chuẩn hoá để so sánh; rỗng nghĩa là không khai. */
function normalizeTabName(name) {
	if (typeof name !== 'string') return '';
	return name.trim();
}

/** Field này có thuộc về phần MASTER không (xét riêng khoản tab, chưa xét kiểu field). */
function isMainTab(field) {
	const t = normalizeTabName(field && field.formTab).toLowerCase();
	if (!t) return true;
	return MAIN_TAB_NAMES.indexOf(t) >= 0;
}

/** Field này có khai TƯỜNG MINH là thuộc dải chân form không. */
function isSummaryTab(field) {
	const t = normalizeTabName(field && field.formTab).toLowerCase();
	if (!t) return false;
	return SUMMARY_TAB_NAMES.indexOf(t) >= 0;
}

/**
 * Chia danh sách field (đã lọc hideInForm) thành ba phần.
 *
 * @param fields          field ĐANG HIỆN của form
 * @param isSummaryField  hàm nhận diện field tổng cộng (tiêm vào, do DataListForm định nghĩa)
 * @returns {{ masterFields, detailFields, summaryFields, extraTabs }}
 *          `extraTabs` = [{ title, fields }] theo THỨ TỰ XUẤT HIỆN của tab trong danh sách field.
 */
function splitVoucherFields(fields, isSummaryField) {
	const isSummary = typeof isSummaryField === 'function' ? isSummaryField : () => false;
	const masterFields = [], detailFields = [], summaryFields = [];
	const tabOrder = [], tabMap = {};

	for (const f of fields || []) {
		if (!f) continue;
		// Lưới chi tiết luôn là TAB CHI TIẾT, dù có khai tab hay không: nó cần cả chiều cao của form,
		// mà dải chân form thì chỉ cao một dòng.
		if (f.type === 'ObjectArray') { detailFields.push(f); continue; }
		// Khai TƯỜNG MINH tab 'summary'/'footer' -> dải chân form. Xét TRƯỚC phần tự nhận diện bên dưới
		// để người khai báo luôn thắng phỏng đoán của hệ thống.
		if (isSummaryTab(f)) { summaryFields.push(f); continue; }
		// Tự nhận diện field tổng cộng (t_tien, t_thue… — luật sẵn có của DataListManager). Field kiểu
		// này luôn ở chân form; khai tab khác cũng không kéo nó vào tab được.
		if (isSummary(f)) { summaryFields.push(f); continue; }
		if (isMainTab(f)) { masterFields.push(f); continue; }

		const title = normalizeTabName(f.formTab);
		if (!tabMap[title]) { tabMap[title] = []; tabOrder.push(title); }
		tabMap[title].push(f);
	}

	return {
		masterFields, detailFields, summaryFields,
		extraTabs: tabOrder.map(t => ({ title: t, fields: tabMap[t] })),
	};
}

/**
 * Thanh tab của form = tab chi tiết TRƯỚC, tab field SAU (đúng thứ tự InputBase dùng).
 * @returns [{ kind: 'detail'|'fields', title, field?, fields? }]
 */
function buildFormTabs(detailFields, extraTabs) {
	const tabs = [];
	(detailFields || []).forEach((f, i) => {
		tabs.push({ kind: 'detail', title: f.label || ('Chi tiết ' + (i + 1)), field: f });
	});
	for (const t of extraTabs || []) {
		tabs.push({ kind: 'fields', title: t.title, fields: t.fields });
	}
	return tabs;
}

/** Chỉ số tab hợp lệ — danh sách tab co lại (đổi cấu hình, ẩn field) không được để trắng màn hình. */
function clampTabIndex(index, tabCount) {
	const n = Number(tabCount) || 0;
	if (n <= 0) return 0;
	const i = Number(index);
	if (!Number.isFinite(i) || i < 0) return 0;
	return Math.min(Math.floor(i), n - 1);
}

/**
 * Field đặc trưng của chứng từ, dùng cho heuristic TỰ TÁCH NHÓM bên dưới.
 */
const MAIN_MASTER_FIELDS = ['so_ct', 'ngay_ct', 'ma_nt', 'ty_gia', 'trang_thai', 'ten_trang_thai'];
const VOUCHER_KEY_FIELDS = ['so_ct', 'ngay_ct', 'ma_nt', 'ty_gia'];

/**
 * Chia phần MASTER thành các khu vực có tiêu đề.
 *
 *   1. Có field khai `formGroup`  -> gom theo `formGroup` (giữ thứ tự khai). LUÔN áp dụng.
 *   2. Không khai, `autoGroup` còn bật, và có đủ field đặc trưng chứng từ
 *                                 -> TỰ tách "Thông tin chung" / "Thông tin khác".
 *   3. Còn lại                    -> `null` = một lưới phẳng, giữ NGUYÊN thứ tự field.
 *
 * `autoGroup: false` là để cấu hình DỊCH TỪ LISTINFO giữ nguyên bố cục gốc của nó: listinfo đã tự sắp
 * bằng `stt`, `grid_configs` và `group`, nên đoán thêm một cách chia nhóm nữa là phá cấu trúc đang có.
 *
 * @returns [{ title, fields }] hoặc null
 */
function buildMasterGroups(masterFields, options) {
	const fields = masterFields || [];
	const opts = options || {};

	if (fields.some(f => f && f.formGroup)) {
		const order = [], map = {};
		for (const f of fields) {
			const g = (f && f.formGroup) || 'Thông tin chung';
			if (!map[g]) { map[g] = []; order.push(g); }
			map[g].push(f);
		}
		return order.map(g => ({ title: g, fields: map[g] }));
	}

	if (opts.autoGroup === false) return null;

	const keys = new Set(fields.map(f => f && f.field));
	if (!VOUCHER_KEY_FIELDS.every(k => keys.has(k))) return null;

	const mainSet = new Set(MAIN_MASTER_FIELDS);
	const main = fields.filter(f => mainSet.has(f && f.field));
	const others = fields.filter(f => !mainSet.has(f && f.field));
	const groups = [{ title: 'Thông tin chung', fields: main }];
	if (others.length) groups.push({ title: 'Thông tin khác', fields: others });
	return groups;
}

/**
 * CỠ Ô MẶC ĐỊNH của field trên form chứng từ (cả phần master lẫn trong các tab field), theo đúng bộ cũ
 * (`GroupTabs.js:217,232-237`):
 * form khổ `lg`/`full` thì 4 field một hàng (md 3), form nhỏ hơn thì 2 field một hàng (md 6).
 *
 * Đây là chỗ quyết định phần master chiếm bao nhiêu diện tích. Mặc định `{xs:12}` (mỗi field một dòng)
 * làm master của chứng từ dài lê thê và đẩy khuất cả thanh tab bên dưới.
 */
const WIDE_FORM_SIZES = ['lg', 'full'];

function defaultVoucherFieldGrid(formSize) {
	const md = WIDE_FORM_SIZES.indexOf(formSize) >= 0 ? 3 : 6;
	return { xs: 12, sm: 12, md, lg: md };
}

/**
 * Cỡ ô của MỘT field trên form chứng từ. `gridEdit` khai ở field (kể cả dịch từ `grid_configs` của
 * listinfo) THẮNG; khoá nào không khai thì lấy của mặc định — nhờ vậy khai `{md:2}` không kéo theo
 * `xs` về 2 trên điện thoại.
 */
function voucherFieldGrid(field, formSize) {
	const base = defaultVoucherFieldGrid(formSize);
	const g = (field && field.gridEdit) || null;
	if (!g) return base;
	const out = { ...base };
	for (const k of ['xs', 'sm', 'md', 'lg']) {
		if (g[k] !== undefined && g[k] !== null) out[k] = g[k];
	}
	return out;
}

/**
 * Form chứng từ mở ở kích thước nào. `/page/voucher/<code>` LUÔN full: chứng từ luôn nhiều dữ liệu,
 * mở ở khổ md là vừa chật vừa phải cuộn.
 */
function resolveFormSize(pageType, declared) {
	if (pageType === 'voucher') return 'full';
	return declared || 'md';
}

export {
	MAIN_TAB_NAMES, SUMMARY_TAB_NAMES, WIDE_FORM_SIZES,
	MAIN_MASTER_FIELDS, VOUCHER_KEY_FIELDS,
	normalizeTabName, isMainTab, isSummaryTab,
	splitVoucherFields, buildFormTabs, clampTabIndex, resolveFormSize,
	defaultVoucherFieldGrid, voucherFieldGrid, buildMasterGroups,
};
